// components/EventRegistrationForm/OTPModal.js
import React from 'react';
import { X, Phone, Mail, Clock, Loader2, Smartphone, AtSign } from 'lucide-react';

const OTPModal = ({
    showOtpModal,
    setShowOtpModal,
    otpType,
    currentContact,
    currentEmail,
    otp,
    handleOtpChange,
    handleOtpKeyDown,
    otpResendTimer,
    resendOtp,
    otpInputRefs
}) => {
    if (!showOtpModal) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
                {/* Modal Header */}
                <div className={`p-6 text-white ${otpType === 'mobile' ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : 'bg-gradient-to-r from-green-600 to-emerald-600'}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            {otpType === 'mobile' ? (
                                <Smartphone className="w-6 h-6 mr-3" />
                            ) : (
                                <AtSign className="w-6 h-6 mr-3" />
                            )}
                            <h3 className="text-xl font-bold">
                                Verify {otpType === 'mobile' ? 'Phone Number' : 'Email Address'}
                            </h3>
                        </div>
                        <button
                            onClick={() => setShowOtpModal(false)}
                            className="text-white hover:text-gray-200 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <p className="text-white/90 text-sm mt-2">
                        Enter the 6-digit OTP sent to {otpType === 'mobile' ? currentContact : currentEmail}
                    </p>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${otpType === 'mobile' ? 'bg-blue-100' : 'bg-green-100'}`}>
                            {otpType === 'mobile' ? (
                                <Phone className="w-10 h-10 text-blue-600" />
                            ) : (
                                <Mail className="w-10 h-10 text-green-600" />
                            )}
                        </div>
                        <h4 className="font-bold text-gray-800 mb-2">Enter Verification Code</h4>
                        <p className="text-gray-600 text-sm">
                            We've sent a 6-digit OTP to your {otpType === 'mobile' ? 'mobile number' : 'email'}
                        </p>
                    </div>

                    {/* OTP Input Fields */}
                    <div className="mb-6">
                        <div className="flex justify-center space-x-3 mb-4">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => otpInputRefs.current[index] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="1"
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    className={`w-12 h-12 text-center text-2xl font-bold border-2 rounded-lg outline-none focus:ring-2 transition-all duration-200 ${otpType === 'mobile' ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-200' : 'border-gray-300 focus:border-green-500 focus:ring-green-200'}`}
                                />
                            ))}
                        </div>

                        <div className="text-center">
                            <p className="text-sm text-gray-500 mb-4">
                                Didn't receive the code?
                                {otpResendTimer > 0 ? (
                                    <span className="text-gray-400 ml-1">
                                        Resend in <Clock className="inline w-3 h-3 ml-1" /> {otpResendTimer}s
                                    </span>
                                ) : (
                                    <button
                                        onClick={resendOtp}
                                        className={`ml-1 font-medium ${otpType === 'mobile' ? 'text-blue-600 hover:text-blue-800' : 'text-green-600 hover:text-green-800'}`}
                                    >
                                        Resend OTP
                                    </button>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Auto-verify note */}
                    {otp.every(digit => digit !== '') && (
                        <div className={`mb-4 p-3 rounded-lg border ${otpType === 'mobile' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="flex items-center">
                                <Loader2 className={`w-4 h-4 mr-2 animate-spin ${otpType === 'mobile' ? 'text-blue-600' : 'text-green-600'}`} />
                                <p className={`text-sm ${otpType === 'mobile' ? 'text-blue-700' : 'text-green-700'}`}>
                                    Auto-verifying OTP...
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OTPModal;